/**
 * What each task board shows: a layout and a project filter, per board
 * frame. A board opens in its `content.default_view`, filtered to its own
 * project (Home's board to all projects). A pick wins on that board until
 * the next project switch, and Reset display goes back to its defaults.
 */
export type TaskViewMode = "board" | "list" | "calendar";

/** Every active project, or one project id. */
export type BoardProjects = "all" | string;

/** One board's defaults, its current layout and filter, and whether the user picked. */
export interface BoardView {
  projectId: string;
  defaultView: TaskViewMode;
  defaultProjects: BoardProjects;
  view: TaskViewMode;
  projects: BoardProjects;
  picked: boolean;
}

export type Boards = Record<string, BoardView>;

/** A board's own project, except Home's, which opens on every project. */
export function boardDefaultProjects(
  projectId: string,
  projects: readonly { id: string; is_home?: boolean }[],
): BoardProjects {
  return projects.some((project) => project.id === projectId && project.is_home)
    ? "all"
    : projectId;
}

/** The board's defaults, unless they are already set or the user picked. */
export function withBoardDefaults(
  boards: Boards,
  frameId: string,
  projectId: string,
  defaultView: TaskViewMode,
  defaultProjects: BoardProjects,
): Boards | null {
  const board = boards[frameId];
  if (
    board?.projectId === projectId &&
    board.defaultView === defaultView &&
    board.defaultProjects === defaultProjects
  )
    return null;
  const picked = board?.projectId === projectId && board.picked;
  return {
    ...boards,
    [frameId]: {
      projectId,
      defaultView,
      defaultProjects,
      view: picked ? board.view : defaultView,
      projects: picked ? board.projects : defaultProjects,
      picked,
    },
  };
}

function withPick(
  boards: Boards,
  frameId: string,
  pick: Pick<BoardView, "view"> | Pick<BoardView, "projects">,
): Boards {
  const board = boards[frameId];
  return board
    ? { ...boards, [frameId]: { ...board, ...pick, picked: true } }
    : boards;
}

/** The user's layout pick on one board. */
export const withPickedView = (
  boards: Boards,
  frameId: string,
  view: TaskViewMode,
): Boards => withPick(boards, frameId, { view });

/** The user's project pick on one board. */
export const withPickedProjects = (
  boards: Boards,
  frameId: string,
  projects: BoardProjects,
): Boards => withPick(boards, frameId, { projects });

/** One board back to its defaults, as if nothing was picked. */
export function withResetBoard(boards: Boards, frameId: string): Boards {
  const board = boards[frameId];
  return board
    ? {
        ...boards,
        [frameId]: {
          ...board,
          view: board.defaultView,
          projects: board.defaultProjects,
          picked: false,
        },
      }
    : boards;
}
